import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, inputCls } from "@/dashboard/ui";
import { RecipientPicker } from "@/dashboard/Marketing";
import { Mail, Plus, Pencil, Trash2, Copy, Clock, XCircle, BarChart3, LayoutTemplate, ImagePlus, Eye, Sparkles } from "lucide-react";

const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Converted", "Lost"];
const EMPTY_C = { name: "", subject: "", preview_text: "", heading: "", body: "", cta_text: "", cta_url: "", image_media_id: null, lead_ids: [], lead_statuses: [] };
const STATUS_TONE = { Draft: "grey", Scheduled: "black", Sending: "amber", Sent: "green", Failed: "red", Cancelled: "outline" };

function EmailPreview({ c }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="email-preview">
      <p className="text-[10px] uppercase tracking-wider text-neutral-400">Preview</p>
      <p className="mt-1 text-sm font-bold text-neutral-900">{c.subject || "(no subject)"}</p>
      {c.preview_text && <p className="text-xs text-neutral-400">{c.preview_text}</p>}
      <div className="mt-3 border-t border-neutral-100 pt-3">
        <p className="text-base font-bold">{c.heading || c.name || "(heading)"}</p>
        <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">{(c.body || "(body)").replace("{{name}}", "Jane")}</p>
        {c.cta_text && c.cta_url && (
          <span className="mt-3 inline-block rounded-full bg-[#111111] px-5 py-2 text-xs font-bold text-white">{c.cta_text}</span>
        )}
        <p className="mt-4 border-t border-neutral-100 pt-2 text-[10px] text-neutral-400">One-click unsubscribe link is added automatically to every email.</p>
      </div>
    </div>
  );
}

function CampaignEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aiWrite = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/marketing/generate", { section: "email", topic: form.name || "customer update" });
      const text = data.content || "";
      const m = text.match(/^Subject:\s*(.+)\n+([\s\S]*)$/i);
      if (m) {
        setForm((f) => ({ ...f, subject: f.subject || m[1].trim(), body: m[2].trim() }));
      } else {
        set("body", text);
      }
      toast.success("AI draft inserted — review and edit before saving");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (initial.id) await api.put(`/email/campaigns/${initial.id}`, payload);
      else await api.post("/email/campaigns", payload);
      toast.success("Campaign saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial.id ? "Edit campaign" : "New email campaign"} onClose={onClose} testid="email-campaign-modal">
      <div className="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Campaign name *</span>
            <input className={inputCls} value={form.name} data-testid="ec-name" onChange={(e) => set("name", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Subject line *</span>
            <input className={inputCls} value={form.subject} data-testid="ec-subject" onChange={(e) => set("subject", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Preview text</span>
            <input className={inputCls} value={form.preview_text} data-testid="ec-preview-text" onChange={(e) => set("preview_text", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Heading inside the email</span>
            <input className={inputCls} value={form.heading} data-testid="ec-heading" onChange={(e) => set("heading", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-xs font-semibold text-neutral-600">
            <span>Body * — use {"{{name}}"} for the contact's first name</span>
            <button type="button" data-testid="ec-ai-write" disabled={aiBusy} onClick={aiWrite} className="inline-flex items-center gap-1 text-[11px] font-bold text-neutral-500 hover:text-[#111111] disabled:opacity-50">
              <Sparkles size={11} /> {aiBusy ? "Writing…" : "Write with AI"}
            </button>
          </span>
          <textarea rows={6} className={inputCls} value={form.body} data-testid="ec-body" onChange={(e) => set("body", e.target.value)} />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Button text</span>
            <input className={inputCls} value={form.cta_text} data-testid="ec-cta-text" onChange={(e) => set("cta_text", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Button link (https)</span>
            <input className={inputCls} value={form.cta_url} data-testid="ec-cta-url" placeholder="https://…" onChange={(e) => set("cta_url", e.target.value)} />
          </label>
        </div>
        <div>
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Segment by CRM status (optional)</span>
          <div className="flex flex-wrap gap-1.5" data-testid="ec-statuses">
            {LEAD_STATUSES.map((s) => (
              <button key={s} type="button" data-testid={`ec-status-${s.toLowerCase()}`}
                onClick={() => set("lead_statuses", form.lead_statuses.includes(s) ? form.lead_statuses.filter((x) => x !== s) : [...form.lead_statuses, s])}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${form.lead_statuses.includes(s) ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Or pick specific recipients</span>
          <RecipientPicker selected={form.lead_ids} onChange={(ids) => set("lead_ids", ids)} />
        </div>
        <p className="text-[11px] text-neutral-400">Contacts without an email address, and anyone who has unsubscribed, are always excluded — at send time, not just here.</p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Btn type="button" variant="ghost" data-testid="ec-preview-toggle" onClick={() => setShowPreview(!showPreview)}>
            <Eye size={13} className="mr-1 inline" /> {showPreview ? "Hide preview" : "Preview email"}
          </Btn>
          <div className="flex gap-2">
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn data-testid="ec-save" disabled={saving || !form.name.trim() || !form.subject.trim() || !form.body.trim()} onClick={save}>
              {saving ? "Saving…" : "Save campaign"}
            </Btn>
          </div>
        </div>
        {showPreview && <EmailPreview c={form} />}
      </div>
    </Modal>
  );
}

// CSS sketch thumbnails — visual preview of each layout without loading the full email
function TemplateThumb({ tpl }) {
  const a = tpl.accent;
  const frame = "rounded-md border border-neutral-200 bg-white p-1.5 h-24 overflow-hidden";
  const bar = (w, c = "#e5e5e5", h = "h-1.5") => <div className={`${h} ${w} rounded-full mb-1`} style={{ background: c }} />;
  return (
    <div className={frame} data-testid={`gallery-thumb-${tpl.id}`}>
      {tpl.layout === "hero" && (<>{tpl.image && <div className="mb-1 h-8 rounded" style={{ background: `linear-gradient(135deg,${a}33,${a}66)` }} />}{bar("w-3/4 mx-auto", a, "h-2")}{bar("w-full")}{bar("w-5/6")}<div className="mx-auto mt-1 h-3 w-12 rounded-full" style={{ background: a }} /></>)}
      {tpl.layout === "split" && (<div className="flex h-full gap-1.5"><div className="w-2/5 rounded" style={{ background: `${a}33` }} /><div className="flex-1">{bar("w-4/5", a, "h-2")}{bar("w-full")}{bar("w-full")}{bar("w-3/5")}<div className="mt-1 h-3 w-10 rounded-full" style={{ background: a }} /></div></div>)}
      {tpl.layout === "banner" && (<><div className="mb-1.5 h-5 rounded" style={{ background: a }} />{bar("w-full")}{bar("w-5/6")}{bar("w-2/3")}<div className="mx-auto mt-1 h-3 w-12 rounded-full" style={{ background: a }} /></>)}
      {tpl.layout === "card" && (<div className="h-full rounded border border-neutral-300 p-1.5 shadow-sm">{bar("w-1/2", a, "h-2")}{bar("w-full")}{bar("w-full")}{bar("w-2/3")}<div className="mx-auto mt-1 h-3 w-12 rounded-full" style={{ background: a }} /></div>)}
      {tpl.layout === "dark" && (<><div className="mb-1 h-7 rounded-t p-1 text-center" style={{ background: "#111111" }}><div className="mx-auto h-2 w-2/3 rounded-full bg-white/70" /></div>{bar("w-full")}{bar("w-4/5")}<div className="mx-auto mt-1 h-3 w-12 rounded-full" style={{ background: a }} /></>)}
      {tpl.layout === "minimal" && (<div className="bg-neutral-50 h-full rounded p-1.5">{bar("w-full", "#d4d4d4")}{bar("w-full", "#d4d4d4")}{bar("w-5/6", "#d4d4d4")}{bar("w-full", "#d4d4d4")}{bar("w-2/3", "#d4d4d4")}<div className="mt-1 h-3 w-10 rounded-full" style={{ background: a }} /></div>)}
    </div>
  );
}

function TemplateGallery({ onUsed }) {
  const [gallery, setGallery] = useState(null);
  const [cat, setCat] = useState("all");
  const [preview, setPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [using, setUsing] = useState(false);

  useEffect(() => {
    api.get("/email/gallery").then((r) => setGallery(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const openPreview = async (tpl) => {
    setPreview(tpl);
    setPreviewHtml("");
    try {
      const { data } = await api.get(`/email/gallery/${tpl.id}/preview`);
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const useTemplate = async () => {
    setUsing(true);
    try {
      await api.post(`/email/gallery/${preview.id}/use`);
      toast.success(`"${preview.name}" added to your templates — customise it below`);
      setPreview(null);
      onUsed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUsing(false);
    }
  };

  if (!gallery) return null;
  const shown = cat === "all" ? gallery.templates : gallery.templates.filter((t) => t.category === cat);

  return (
    <div className="space-y-4" data-testid="template-gallery">
      <Card className="p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Template gallery — 30 professional designs</p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="gallery-categories">
          <button data-testid="gallery-cat-all" onClick={() => setCat("all")}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${cat === "all" ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>All</button>
          {gallery.categories.map((c) => (
            <button key={c.id} data-testid={`gallery-cat-${c.id}`} onClick={() => setCat(c.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${cat === c.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>{c.label}</button>
          ))}
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4" data-testid="gallery-grid">
        {shown.map((t) => (
          <button key={t.id} onClick={() => openPreview(t)} data-testid={`gallery-item-${t.id}`}
            className="group rounded-2xl border border-neutral-200 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-neutral-400 hover:shadow-md">
            <TemplateThumb tpl={t} />
            <p className="mt-2 text-xs font-bold text-[#111111]">{t.name}</p>
            <p className="text-[10px] text-neutral-400">{t.audience}</p>
          </button>
        ))}
      </div>

      {preview && (
        <Modal title={preview.name} onClose={() => setPreview(null)} testid="gallery-preview-modal">
          <div className="space-y-3">
            <p className="text-xs text-neutral-500" data-testid="gallery-preview-subject">Suggested subject: {preview.subject}</p>
            <div className="overflow-hidden rounded-xl border border-neutral-200" data-testid="gallery-preview-frame">
              {previewHtml
                ? <iframe title="Email preview" srcDoc={previewHtml} className="h-[420px] w-full" sandbox="" />
                : <p className="py-16 text-center text-xs text-neutral-400">Rendering preview…</p>}
            </div>
            <p className="text-[11px] text-neutral-400">Preview shows sample text — the real email uses your business name and each recipient's first name. The unsubscribe link is added automatically.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setPreview(null)}>Close</Btn>
              <Btn data-testid="gallery-use" disabled={using} onClick={useTemplate}>{using ? "Adding…" : "Use this template"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function EmailPanel() {
  const [provider, setProvider] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tplEditing, setTplEditing] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [tplPreview, setTplPreview] = useState(null);
  const [schedAt, setSchedAt] = useState("");
  const [stats, setStats] = useState(null);
  const [audience, setAudience] = useState(null);
  const [tab, setTab] = useState("campaigns");

  const load = () => {
    api.get("/email/provider").then((r) => setProvider(r.data)).catch(() => {});
    api.get("/email/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch((e) => toast.error(formatApiError(e)));
    api.get("/email/templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const schedule = async () => {
    try {
      await api.post(`/email/campaigns/${scheduling.id}/schedule`, { scheduled_at: schedAt, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      toast.success("Campaign scheduled — it sends only when email sending is approved and enabled");
      setScheduling(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const act = async (c, path, msg) => {
    try {
      await api.post(`/email/campaigns/${c.id}/${path}`);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/email/campaigns/${c.id}`);
    load();
  };

  const showStats = async (c) => {
    const { data } = await api.get(`/email/campaigns/${c.id}/stats`);
    setStats({ c, ...data });
  };

  const showAudience = async (c) => {
    try {
      const { data } = await api.get(`/email/campaigns/${c.id}/audience`);
      setAudience({ c, ...data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveTemplate = async () => {
    try {
      if (tplEditing.id) await api.put(`/email/templates/${tplEditing.id}`, tplEditing);
      else await api.post("/email/templates", tplEditing);
      toast.success("Template saved");
      setTplEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="email-panel">
      {provider && (
        <Notice testid="email-provider-note"
          title={provider.state === "needs_setup" ? "Email delivery needs setup" : provider.sending_enabled ? "Email delivery is ready" : "Email delivery is configured but sending is off"}
          body={provider.state === "needs_setup"
            ? "No email provider key is configured. Campaigns can be drafted and scheduled now; they send only after a provider is connected and sending is approved."
            : "Provider connected. Campaign sends happen only after the platform owner enables sending — nothing goes out automatically without that approval."} />
      )}
      <div className="flex items-center justify-between">
        <div className="flex rounded-full border border-neutral-300 p-0.5" data-testid="email-subtabs">
          {[["campaigns", "Campaigns"], ["templates", "Templates"]].map(([id, l]) => (
            <button key={id} data-testid={`email-subtab-${id}`} onClick={() => setTab(id)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${tab === id ? "bg-[#111111] text-white" : "text-neutral-500"}`}>{l}</button>
          ))}
        </div>
        <Btn data-testid="email-new-button" onClick={() => tab === "campaigns" ? setEditing({ ...EMPTY_C }) : setTplEditing({ name: "", subject: "", preview_text: "", heading: "", body: "", cta_text: "", cta_url: "" })}>
          <Plus size={15} /> {tab === "campaigns" ? "New campaign" : "New template"}
        </Btn>
      </div>

      {tab === "campaigns" && (
        campaigns === null ? <p className="py-8 text-center text-sm text-neutral-400">Loading…</p> : campaigns.length === 0 ? (
          <Empty testid="email-campaigns-empty" icon={Mail} title="No campaigns yet" body="Create a campaign with subject, preview text, body and a call to action, then choose your CRM audience. Nothing is sent without approval." />
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <Card key={c.id} className="p-5" data-testid={`email-campaign-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                      <Pill tone={STATUS_TONE[c.status]} data-testid={`email-status-${c.id}`}>{c.status}</Pill>
                      {c.status === "Scheduled" && <Pill tone="outline">{c.scheduled_at?.replace("T", " ").slice(0, 16)} {c.timezone}</Pill>}
                    </div>
                    <p className="mt-1 text-xs text-neutral-500">Subject: {c.subject}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Audience" data-testid={`email-audience-${c.id}`} onClick={() => showAudience(c)}><Eye size={14} /></button>
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Stats" data-testid={`email-stats-${c.id}`} onClick={() => showStats(c)}><BarChart3 size={14} /></button>
                    {!["Sending", "Sent"].includes(c.status) && (
                      <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Edit" data-testid={`email-edit-${c.id}`} onClick={() => setEditing({ ...c })}><Pencil size={14} /></button>
                    )}
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Duplicate" data-testid={`email-duplicate-${c.id}`} onClick={() => act(c, "duplicate", "Campaign duplicated")}><Copy size={14} /></button>
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`email-delete-${c.id}`} onClick={() => remove(c)}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Draft", "Failed", "Cancelled"].includes(c.status) && (
                    <Btn variant="ghost" data-testid={`email-schedule-${c.id}`} onClick={() => { setScheduling(c); setSchedAt(""); }}>
                      <Clock size={13} className="mr-1 inline" /> Schedule send
                    </Btn>
                  )}
                  {c.status === "Scheduled" && (
                    <Btn variant="ghost" data-testid={`email-cancel-${c.id}`} onClick={() => act(c, "cancel", "Schedule cancelled — nothing was sent")}>
                      <XCircle size={13} className="mr-1 inline" /> Cancel schedule
                    </Btn>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "templates" && (
        <div className="space-y-6">
          <TemplateGallery onUsed={load} />
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-500" data-testid="my-templates-title">Your templates</p>
        {templates.length === 0 ? (
          <Empty testid="email-templates-empty" icon={LayoutTemplate} title="No templates yet" body="Save reusable subject/body layouts, then start campaigns from them." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((t) => (
              <Card key={t.id} className="p-5" data-testid={`email-template-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-[#111111]">{t.name}</p>
                    <p className="mt-0.5 text-xs text-neutral-500">{t.subject}</p>
                  </div>
                  <div className="flex gap-1">
                    {t.design && (
                      <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid={`template-preview-${t.id}`}
                        onClick={async () => { try { const { data } = await api.get(`/email/templates/${t.id}/render`); setTplPreview({ name: t.name, html: data.html }); } catch (e) { toast.error(formatApiError(e)); } }}><Eye size={14} /></button>
                    )}
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid={`template-edit-${t.id}`} onClick={() => setTplEditing({ ...t })}><Pencil size={14} /></button>
                    <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" data-testid={`template-delete-${t.id}`}
                      onClick={async () => { if (window.confirm(`Delete template "${t.name}"?`)) { await api.delete(`/email/templates/${t.id}`); load(); } }}><Trash2 size={14} /></button>
                  </div>
                </div>
                <Btn variant="ghost" className="mt-3" data-testid={`template-use-${t.id}`}
                  onClick={() => setEditing({ ...EMPTY_C, name: t.name, subject: t.subject, preview_text: t.preview_text, heading: t.heading, body: t.body, cta_text: t.cta_text, cta_url: t.cta_url })}>
                  Start campaign from template
                </Btn>
              </Card>
            ))}
          </div>
        )}
          </div>
        </div>
      )}

      {editing && <CampaignEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {tplEditing && (
        <Modal title={tplEditing.id ? "Edit template" : "New template"} onClose={() => setTplEditing(null)} testid="email-template-modal">
          <div className="space-y-3">
            <input className={inputCls} placeholder="Template name *" value={tplEditing.name} data-testid="et-name" onChange={(e) => setTplEditing((t) => ({ ...t, name: e.target.value }))} />
            <input className={inputCls} placeholder="Subject *" value={tplEditing.subject} data-testid="et-subject" onChange={(e) => setTplEditing((t) => ({ ...t, subject: e.target.value }))} />
            <input className={inputCls} placeholder="Heading" value={tplEditing.heading} onChange={(e) => setTplEditing((t) => ({ ...t, heading: e.target.value }))} />
            <textarea rows={5} className={inputCls} placeholder="Body * (use {{name}} for first name)" value={tplEditing.body} data-testid="et-body" onChange={(e) => setTplEditing((t) => ({ ...t, body: e.target.value }))} />
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={inputCls} placeholder="Button text" value={tplEditing.cta_text} onChange={(e) => setTplEditing((t) => ({ ...t, cta_text: e.target.value }))} />
              <input className={inputCls} placeholder="Button link (https)" value={tplEditing.cta_url} data-testid="et-cta-url" onChange={(e) => setTplEditing((t) => ({ ...t, cta_url: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setTplEditing(null)}>Cancel</Btn>
              <Btn data-testid="et-save" disabled={!tplEditing.name.trim() || !tplEditing.subject.trim() || !tplEditing.body.trim()} onClick={saveTemplate}>Save template</Btn>
            </div>
          </div>
        </Modal>
      )}

      {tplPreview && (
        <Modal title={tplPreview.name} onClose={() => setTplPreview(null)} testid="template-render-modal">
          <div className="overflow-hidden rounded-xl border border-neutral-200" data-testid="template-render-frame">
            {tplPreview.html
              ? <iframe title="Template preview" srcDoc={tplPreview.html} className="h-[420px] w-full" sandbox="" />
              : <p className="py-16 text-center text-xs text-neutral-400">No visual design attached to this template.</p>}
          </div>
        </Modal>
      )}

      {scheduling && (
        <Modal title={`Schedule — ${scheduling.name}`} onClose={() => setScheduling(null)} testid="email-schedule-modal">          <div className="space-y-3">
            <p className="text-sm text-neutral-600">The send runs at this time even with your browser closed — but only after the platform owner enables email sending. Until then the campaign stays queued and nothing is sent.</p>
            <input type="datetime-local" className={inputCls} value={schedAt} data-testid="email-schedule-at" onChange={(e) => setSchedAt(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setScheduling(null)}>Cancel</Btn>
              <Btn data-testid="email-schedule-confirm" disabled={!schedAt} onClick={schedule}>Schedule</Btn>
            </div>
          </div>
        </Modal>
      )}

      {audience && (
        <Modal title={`Audience — ${audience.c.name}`} onClose={() => setAudience(null)} testid="email-audience-modal">
          <p className="text-sm font-bold text-[#111111]" data-testid="email-audience-count">{audience.count} eligible recipient{audience.count === 1 ? "" : "s"}</p>
          <div className="mt-2 space-y-1">
            {audience.sample.map((l) => <p key={l.id} className="text-xs text-neutral-600">{l.full_name} · {l.email}</p>)}
          </div>
          <p className="mt-3 text-[11px] text-neutral-400">{audience.note}</p>
        </Modal>
      )}

      {stats && (
        <Modal title={`Stats — ${stats.c.name}`} onClose={() => setStats(null)} testid="email-stats-modal">
          <div className="grid grid-cols-3 gap-3 text-center" data-testid="email-stats-body">
            {[["Queued", stats.queued], ["Sent", stats.sent], ["Failed", stats.failed], ["Delivered", stats.delivered], ["Bounced", stats.bounced], ["Unsubscribed", stats.unsubscribed]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-neutral-50 p-3">
                <p className="text-xl font-extrabold" data-testid={`email-stat-${l.toLowerCase()}`}>{v}</p>
                <p className="text-[11px] text-neutral-500">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-neutral-400">Sent = accepted by the provider. Delivered and bounced stay at zero until provider webhooks are configured — they are never estimated.</p>
        </Modal>
      )}
    </div>
  );
}
